import { SELECTION_CAP } from 'areas/search/hooks';
import { faMailBulk, faShareSquare, faHashtag, faExternalLink, faCommentAltLines, faChalkboardTeacher, faDoorOpen } from '@fortawesome/pro-light-svg-icons';
import { IAction, ISelectedEntity } from './interfaces';
import { AssignCompanyTagsDialog } from '../modals/assign-company-tags-dialog/assign-company-tags-dialog';
import { AssignCaseworkerCompanyDialog } from '../modals/assign-caseworker-dialog/assign-caseworker-company-dialog';
import { ShowCompaniesDialog } from '../modals/show-company-dialog/show-companies-dialog';
import { ChangeCourseEducatorsDialog } from '../modals/course-bulk-edit/change-course-educators-dialog';
import { ChangeCourseClassroomDialog } from '../modals/course-bulk-edit/change-course-classroom-dialog';
import { ChangeLessonEducatorsDialog } from '../modals/course-bulk-edit/change-lesson-educators-dialog';
import { ChangeLessonClassroomDialog } from '../modals/course-bulk-edit/change-lesson-classroom-dialog';
import { ResourceOperations, ResourceTypes } from '../../../authentication/resources';

export const attendanceFeatureName = 'EnableAttendance';
export const attendanceLessonAndCourseMassActionFeatureName = 'EnableAttendanceLessonAndCourseMassAction';

const attendanceWriteAccess = [{ resourceType: ResourceTypes.Attendance, resourceOperation: ResourceOperations.Write }];

// Shown in the company menu of course and lesson worklists, where a row's root entity is its company
function disabledReasonForBulkEdit(selectedItemsCount: number, selectedItems: ISelectedEntity[], severalCompaniesReason: string) {
  if (selectedItemsCount > SELECTION_CAP) {
    return `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}`;
  }
  if (new Set(selectedItems.map(item => item.rootEntityId)).size > 1) {
    return severalCompaniesReason;
  }
  return '';
}

function entityIdsOf(selectedEntities: ISelectedEntity[]) {
  return selectedEntities.map(entity => entity.entityId);
}

function companyIdOf(selectedEntities: ISelectedEntity[]) {
  return selectedEntities[0]?.rootEntityId ?? '';
}

export const actions: IAction[] = [
  {
    context: 'company',
    id: 'ShowCompanies',
    displayName: 'Vis virksomheder',
    action: async selectedEntries => <ShowCompaniesDialog companyIds={selectedEntries} />,
    icon: faCommentAltLines,
    isVisible: () => true,
    contextOnlyAction: true,
  },
  {
    context: 'company',
    id: 'CreateCrowdLetterCompany',
    displayName: 'Dan breve',
    action: selectedEntries => {
      const url = `${window.location.origin}/company/crowd-letter`;
      const popup = window.open(url);
      const timer = setInterval(() => {
        if (popup) {
          popup.postMessage({ type: 'crowdLetterCompany', payload: selectedEntries }, window.location.origin);

          clearInterval(timer);
        }
      }, 7000);
    },
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    icon: faMailBulk,
    showOnUniverseAction: true,
  },
  {
    context: 'company',
    id: 'CompanyAssignTeamOrUser',
    displayName: 'Tildel',
    icon: faShareSquare,
    action: selectedEntries => <AssignCaseworkerCompanyDialog companyIds={selectedEntries} />,

    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'company',
    id: 'TagCompany',
    displayName: 'Tag virksomheder',
    icon: faHashtag,
    action: selectedEntries => <AssignCompanyTagsDialog companyIds={selectedEntries} />,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'company',
    id: 'ShowLink',
    displayName: 'Vis link',
    action: selectedEntities => {
      let preText = '';
      let text = '';
      selectedEntities.forEach(entry => {
        const link = `${window.location.origin}/company/${entry.replace(/[{}]/g, '')}/details`;
        text += `${preText}${link}`;
        preText = '\r\n\r\n';
      });
      // eslint-disable-next-line no-alert
      window.prompt('Kopiér link til udklipsholder: Ctrl+C, Enter', text);
    },
    icon: faExternalLink,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > 10 ? 'Antallet af valgte elementer må ikke overstige 10' : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'company',
    id: 'ChangeCourseEducators',
    displayName: 'Ret undervisere',
    icon: faChalkboardTeacher,
    action: (_selectedIds, _selectedRootIds, _isFeatureEnabled, _replaceUrl, selectedEntities, onActionCompleted) => (
      <ChangeCourseEducatorsDialog courseIds={entityIdsOf(selectedEntities)} companyId={companyIdOf(selectedEntities)} onCompleted={onActionCompleted} />
    ),
    isVisible: (selectedItemsCount, isFeatureEnabled) =>
      isFeatureEnabled(attendanceFeatureName) && isFeatureEnabled(attendanceLessonAndCourseMassActionFeatureName) && selectedItemsCount > 0,
    isDisabled: (selectedItemsCount, _rowCount, _isLoading, selectedItems) =>
      disabledReasonForBulkEdit(selectedItemsCount, selectedItems, 'De valgte hold hører til flere virksomheder'),
    requiredAccess: attendanceWriteAccess,
    contextOnlyAction: true,
    showOnlyForContext: 'courses',
    showInBottomMenuSection: true,
  },
  {
    context: 'company',
    id: 'ChangeCourseClassroom',
    displayName: 'Skift lokale',
    icon: faDoorOpen,
    action: (_selectedIds, _selectedRootIds, _isFeatureEnabled, _replaceUrl, selectedEntities, onActionCompleted) => (
      <ChangeCourseClassroomDialog courseIds={entityIdsOf(selectedEntities)} companyId={companyIdOf(selectedEntities)} onCompleted={onActionCompleted} />
    ),
    isVisible: (selectedItemsCount, isFeatureEnabled) =>
      isFeatureEnabled(attendanceFeatureName) && isFeatureEnabled(attendanceLessonAndCourseMassActionFeatureName) && selectedItemsCount > 0,
    isDisabled: (selectedItemsCount, _rowCount, _isLoading, selectedItems) =>
      disabledReasonForBulkEdit(selectedItemsCount, selectedItems, 'De valgte hold hører til flere virksomheder'),
    requiredAccess: attendanceWriteAccess,
    contextOnlyAction: true,
    showOnlyForContext: 'courses',
    showInBottomMenuSection: true,
  },
  {
    context: 'company',
    id: 'ChangeLessonEducators',
    displayName: 'Ret undervisere',
    icon: faChalkboardTeacher,
    action: (_selectedIds, _selectedRootIds, _isFeatureEnabled, _replaceUrl, selectedEntities, onActionCompleted) => (
      <ChangeLessonEducatorsDialog lessonIds={entityIdsOf(selectedEntities)} companyId={companyIdOf(selectedEntities)} onCompleted={onActionCompleted} />
    ),
    isVisible: (selectedItemsCount, isFeatureEnabled) =>
      isFeatureEnabled(attendanceFeatureName) && isFeatureEnabled(attendanceLessonAndCourseMassActionFeatureName) && selectedItemsCount > 0,
    isDisabled: (selectedItemsCount, _rowCount, _isLoading, selectedItems) =>
      disabledReasonForBulkEdit(selectedItemsCount, selectedItems, 'De valgte lektioner hører til flere virksomheder'),
    requiredAccess: attendanceWriteAccess,
    contextOnlyAction: true,
    showOnlyForContext: 'courses.courselessons',
    showInBottomMenuSection: true,
  },
  {
    context: 'company',
    id: 'ChangeLessonClassroom',
    displayName: 'Skift lokale',
    icon: faDoorOpen,
    action: (_selectedIds, _selectedRootIds, _isFeatureEnabled, _replaceUrl, selectedEntities, onActionCompleted) => (
      <ChangeLessonClassroomDialog lessonIds={entityIdsOf(selectedEntities)} companyId={companyIdOf(selectedEntities)} onCompleted={onActionCompleted} />
    ),
    isVisible: (selectedItemsCount, isFeatureEnabled) =>
      isFeatureEnabled(attendanceFeatureName) && isFeatureEnabled(attendanceLessonAndCourseMassActionFeatureName) && selectedItemsCount > 0,
    isDisabled: (selectedItemsCount, _rowCount, _isLoading, selectedItems) =>
      disabledReasonForBulkEdit(selectedItemsCount, selectedItems, 'De valgte lektioner hører til flere virksomheder'),
    requiredAccess: attendanceWriteAccess,
    contextOnlyAction: true,
    showOnlyForContext: 'courses.courselessons',
    showInBottomMenuSection: true,
  },
];
